import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, Check, ShieldCheck, ArrowRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import MagneticButton from "@/components/site/MagneticButton";
import { getIndustry, getServices, getCaseStudies } from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function IndustryDetail() {
  const { slug } = useParams();
  const [ind, setInd] = useState(null);
  const [services, setServices] = useState([]);
  const [cases, setCases] = useState([]);

  useEffect(() => {
    setInd(null);
    getIndustry(slug).then(setInd).catch(() => setInd({ notFound: true }));
    getServices().then(setServices).catch(() => {});
    getCaseStudies().then(setCases).catch(() => {});
  }, [slug]);

  useSEO({
    title: ind?.seo_title || (ind?.name ? `${ind.name} Cyber Security | RockLadder` : "Industry"),
    description: ind?.seo_description || ind?.short_desc,
    canonical: `https://rockladder.com/industries/${slug}`,
  });

  if (ind?.notFound) {
    return (
      <div className="pt-40 pb-24 text-center">
        <Link to="/industries" className="text-gold">Back to Industries</Link>
      </div>
    );
  }
  if (!ind) return <div className="pt-40 pb-24 text-center text-inkText-muted">Loading…</div>;

  const relatedCases = cases.filter(
    (c) => c.industry?.toLowerCase() === ind.name.toLowerCase() ||
           c.industry?.toLowerCase().includes(ind.name.toLowerCase().split(" ")[0])
  ).slice(0, 3);

  const applicable = services.slice(0, 6);

  return (
    <>
      <PageHero
        eyebrow="INDUSTRY"
        title={ind.name}
        subtitle={ind.tagline}
        breadcrumb={[{ label: "Industries", to: "/industries" }, { label: ind.name }]}
      />

      <section className="relative py-16">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {/* Challenges */}
          <div className="glass-panel rounded-2xl p-7 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gold/25 bg-ink-panel/60 text-gold">
                <AlertTriangle className="h-4 w-4" strokeWidth={1.8} />
              </span>
              <h2 className="font-display text-xl md:text-2xl text-inkText-primary">Challenges</h2>
            </div>
            <ul className="space-y-3">
              {(ind.challenges || []).map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-inkText-muted leading-relaxed">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-violet2 flex-shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions */}
          <div className="glass-panel rounded-2xl p-7 md:p-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gold/25 bg-ink-panel/60 text-gold">
                <ShieldCheck className="h-4 w-4" strokeWidth={1.8} />
              </span>
              <h2 className="font-display text-xl md:text-2xl text-inkText-primary">How we deliver</h2>
            </div>
            <ul className="space-y-3">
              {(ind.solutions || []).map((s) => (
                <li key={s} className="flex items-start gap-3 text-sm text-inkText-primary/90 leading-relaxed">
                  <Check className="h-4 w-4 mt-0.5 text-gold flex-shrink-0" strokeWidth={2.4} />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Compliance focus */}
      <section className="relative py-8">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="glass-panel-strong rounded-2xl p-6 md:p-8">
            <div className="font-mono text-[10px] tracking-[0.28em] text-gold-body mb-4">
              COMPLIANCE FOCUS
            </div>
            <div className="flex flex-wrap gap-2">
              {(ind.compliance_focus || []).map((c) => (
                <span
                  key={c}
                  className="font-mono text-[11px] tracking-widest text-inkText-primary/90 border border-gold/25 rounded-full px-3 py-1.5"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Applicable services */}
      <section className="relative py-16 md:py-24">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-8">
            Applicable services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {applicable.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="glass-panel rounded-xl p-5 group hover:border-gold transition-colors"
                data-testid={`industry-service-${s.slug}`}
              >
                <div className="font-display text-base text-inkText-primary group-hover:text-gold">
                  {s.name}
                </div>
                <p className="mt-2 text-xs text-inkText-muted line-clamp-2">{s.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Related case studies */}
      {relatedCases.length > 0 && (
        <section className="relative py-16">
          <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
            <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-8">
              Related case studies
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedCases.map((c) => (
                <Link
                  key={c.slug}
                  to={`/case-studies/${c.slug}`}
                  className="glass-panel rounded-xl p-5 group hover:border-gold transition-colors"
                >
                  <div className="font-mono text-[10px] tracking-[0.24em] text-gold-body mb-3">
                    {c.industry.toUpperCase()}
                  </div>
                  <div className="font-display text-base text-inkText-primary group-hover:text-gold">
                    {c.title}
                  </div>
                  <p className="mt-3 text-xs text-inkText-muted line-clamp-3">{c.summary}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative py-16">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="glass-panel-strong rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="font-display text-xl md:text-2xl text-inkText-primary">
                Working in {ind.name}? Let&apos;s talk.
              </div>
              <p className="text-sm text-inkText-muted mt-1">
                Sector-specific working session with a senior engineer.
              </p>
            </div>
            <MagneticButton as={Link} to="/contact" variant="primary">
              Book a Consultation <ArrowRight className="h-4 w-4" />
            </MagneticButton>
          </div>
        </div>
      </section>
    </>
  );
}
